import {
  CheckCircleIcon,
  DownloadIcon,
  ExclamationIcon,
} from '@heroicons/react/outline';
import { useEffect, useState } from 'react';

import { CONSENT_CLAUSES, MEDICAL_CONDITIONS } from '@/constants/intakeForm';
import { patientIntakeService } from '@/services';

// Shared component used by both the Provider AppointmentModal and the
// SuperAdmin AppointmentModal. Renders the submitted intake inline and
// surfaces a Download PDF button that gracefully handles the still-generating
// case (status=completed but pdf_s3_key not yet populated).

const Row = ({ label, children }) => (
  <div className="py-2 border-b border-gray-100 last:border-b-0">
    <div className="text-sm font-semibold text-mmj-header">{label}</div>
    <div className="text-base text-mmj-header break-words">
      {children || <span className="text-gray-400">—</span>}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-4">
    <h4 className="text-base font-bold text-mmj-header border-b border-gray-200 pb-1 mb-2">
      {title}
    </h4>
    <div>{children}</div>
  </div>
);

// Render any stored date_of_birth in the canonical MM/DD/YYYY format the
// state programs use. Accepts the new MM/DD/YYYY storage format unchanged,
// and migrates legacy ISO (YYYY-MM-DD) records on the fly for older intakes.
// Anything unrecognized is rendered verbatim so we never hide real data.
const formatDobMMDDYYYY = (raw) => {
  if (typeof raw !== 'string' || !raw.trim()) return '';
  const trimmed = raw.trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return trimmed;
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  return trimmed;
};

const downloadBlob = async (url, filename) => {
  // Fetch first so we can validate the response is actually a PDF before
  // saving. The previous implementation blindly converted any 2xx (or even
  // some 4xx) response into a blob and saved it as .pdf, which is why an
  // S3 AccessDenied XML body ended up looking like a "broken" PDF in the
  // browser. If anything is off, surface the raw URL in a new tab so the
  // browser can show the error response and the user can copy the link.
  try {
    const response = await fetch(url);
    const contentType = (
      response.headers.get('content-type') || ''
    ).toLowerCase();
    if (!response.ok) {
      throw new Error(`fetch returned ${response.status}`);
    }
    const isPdf =
      contentType.includes('application/pdf') ||
      contentType.includes('application/octet-stream') ||
      contentType === '';
    if (!isPdf) {
      throw new Error(`unexpected content-type ${contentType}`);
    }
    const blob = await response.blob();
    if (blob.size < 1024) {
      // Tiny payload is almost certainly an S3 error envelope, not a real PDF.
      throw new Error(`pdf payload too small (${blob.size} bytes)`);
    }
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    // Open the signed URL directly so the browser can render whatever S3
    // actually returned (PDF inline, or the error XML body if it's broken).
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const PatientIntakeView = ({ appointmentId, patientName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [intake, setIntake] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    if (!appointmentId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await patientIntakeService.getIntake(appointmentId);
        if (cancelled) return;
        setIntake(data);
      } catch (err) {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        if (status === 404) {
          setError('No intake form exists for this appointment.');
        } else if (status === 403 || status === 401) {
          setError('You do not have access to this intake form.');
        } else {
          setError('We could not load the intake form. Try again later.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  if (loading) {
    return (
      <div className="border-t border-gray-200 pt-4 mt-2">
        <h3 className="text-lg font-bold text-mmj-header mb-2">Intake form</h3>
        <div className="text-gray-500">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-t border-gray-200 pt-4 mt-2">
        <h3 className="text-lg font-bold text-mmj-header mb-2">Intake form</h3>
        <div className="text-gray-600">{error}</div>
      </div>
    );
  }

  if (!intake) return null;

  // The backend column is JSONB but, depending on the driver and how it's
  // serialized back through the Lambda, `responses` may arrive here as either
  // a plain object or a JSON-encoded string. Coerce defensively so the
  // provider never sees a screen full of dashes when the data is actually
  // stored correctly in the DB.
  let responses = intake.responses;
  if (typeof responses === 'string') {
    try {
      const parsed = JSON.parse(responses);
      responses = parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      responses = {};
    }
  }
  if (!responses || typeof responses !== 'object' || Array.isArray(responses)) {
    responses = {};
  }

  const hasAnyAnswers = Object.keys(responses).some(
    (k) =>
      !!responses[k] &&
      (Array.isArray(responses[k]) ? responses[k].length > 0 : true),
  );

  if (intake.status !== 'completed') {
    return (
      <div className="border-t border-gray-200 pt-4 mt-2">
        <h3 className="text-lg font-bold text-mmj-header mb-2">Intake form</h3>
        <div className="flex items-start space-x-3 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
          <ExclamationIcon
            className="w-6 h-6 text-yellow-700 flex-shrink-0"
            aria-hidden="true"
          />
          <div>
            <div className="font-semibold text-yellow-900">
              Patient has not completed the intake yet
            </div>
            <div className="text-sm text-yellow-800 mt-1">
              Status:{' '}
              {intake.status === 'in_progress' ? 'In progress' : 'Not started'}
              {intake.updated_at &&
                ` · Last activity ${new Date(
                  intake.updated_at,
                ).toLocaleString()}`}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDownload = async () => {
    if (!intake.pdf_s3_key) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const url = await patientIntakeService.getPdfViewUrl(intake.pdf_s3_key);
      if (!url) {
        setDownloadError(
          'The PDF link could not be generated. The presign endpoint did not return a signed URL for this file. (Backend: check that patient_documents/presign accepts the "intakes/" key prefix and that its IAM role has s3:GetObject on the intake objects.)',
        );
      } else {
        const fname = `intake_${(patientName || 'patient').replace(
          /\W+/g,
          '_',
        )}_${appointmentId.slice(0, 8)}.pdf`;
        await downloadBlob(url, fname);
      }
    } catch (err) {
      setDownloadError(
        'Something went wrong while downloading the PDF. Please try again.',
      );
    }
    setDownloading(false);
  };

  const submittedAt = intake.signed_at
    ? new Date(intake.signed_at).toLocaleString()
    : '';

  const conditions = Array.isArray(responses.conditions)
    ? responses.conditions
    : [];
  const conditionLabel = MEDICAL_CONDITIONS.filter((c) =>
    conditions.includes(c),
  ).join(', ');

  return (
    <div className="border-t border-gray-200 pt-4 mt-2">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-lg font-bold text-mmj-header flex items-center space-x-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            <span>Intake form submitted</span>
          </h3>
          {submittedAt && (
            <div className="text-sm text-gray-600 mt-1">
              Signed by {intake.signed_name || '—'} on {submittedAt}
            </div>
          )}
        </div>
        {intake.pdf_s3_key ? (
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="button button-primary flex items-center space-x-2"
          >
            <DownloadIcon className="w-4 h-4" aria-hidden="true" />
            <span>{downloading ? 'Preparing…' : 'Download PDF'}</span>
          </button>
        ) : (
          <button
            type="button"
            disabled
            className="button button-secondary"
            title="The PDF is still generating. Refresh in a moment."
          >
            PDF generating, refresh in a moment
          </button>
        )}
      </div>

      {downloadError && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 mb-4 text-sm text-red-900">
          {downloadError}
        </div>
      )}

      {!hasAnyAnswers && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-4 text-sm text-yellow-900">
          The intake was signed but no question responses were recorded. This
          can happen if the patient submitted an empty form or if a previous
          client version stored a corrupted draft. Ask the patient to redo the
          intake from their dashboard.
        </div>
      )}

      <Section title="Patient Information">
        <Row label="Date of birth">
          {formatDobMMDDYYYY(responses.date_of_birth)}
        </Row>
        <Row label="Gender">{responses.gender}</Row>
        <Row label="Address">
          {[responses.address, responses.city, responses.zip]
            .filter(Boolean)
            .join(', ')}
        </Row>
        <Row label="Height">
          {responses.height_ft || responses.height_in
            ? `${responses.height_ft || '?'} ft ${
                responses.height_in || '?'
              } in`
            : ''}
        </Row>
        <Row label="Weight">
          {responses.weight_lbs ? `${responses.weight_lbs} lbs` : ''}
        </Row>
      </Section>

      <Section title="Medical History">
        <Row label="Conditions">{conditionLabel}</Row>
        <Row label="Other / details">{responses.conditions_other}</Row>
        <Row label="Current medications">{responses.current_medications}</Row>
        <Row label="Allergies">{responses.allergies}</Row>
        <Row label="Surgeries / hospitalizations">{responses.surgeries}</Row>
      </Section>

      <Section title="Lifestyle">
        <Row label="Exercise">
          {responses.exercises_regularly === true
            ? 'Yes'
            : responses.exercises_regularly === false
            ? 'No'
            : ''}
        </Row>
        <Row label="Tobacco">
          {responses.tobacco === true
            ? 'Yes'
            : responses.tobacco === false
            ? 'No'
            : ''}
        </Row>
        <Row label="Alcohol">
          {responses.drinks_alcohol === false
            ? 'No'
            : responses.alcohol ||
              (responses.drinks_alcohol === true ? 'Yes' : '')}
        </Row>
        <Row label="Family medical history">{responses.family_history}</Row>
      </Section>

      <Section title="Mental Health">
        <Row label="Reported">{(responses.mental_health || []).join(', ')}</Row>
      </Section>

      <Section title="Primary Care & Certification">
        <Row label="Has PCP">
          {responses.has_pcp === true
            ? 'Yes'
            : responses.has_pcp === false
            ? 'No'
            : ''}
        </Row>
        <Row label="PCP name">{responses.pcp_name}</Row>
        <Row label="Last visit">{responses.last_pcp_visit}</Row>
        <Row label="Renewal?">
          {responses.is_renewal === true
            ? 'Yes'
            : responses.is_renewal === false
            ? 'No'
            : ''}
        </Row>
        {responses.gender === 'female' && (
          <>
            <Row label="Pregnant?">
              {responses.pregnant === true
                ? 'Yes'
                : responses.pregnant === false
                ? 'No'
                : ''}
            </Row>
            <Row label="Breastfeeding?">
              {responses.breastfeeding === true
                ? 'Yes'
                : responses.breastfeeding === false
                ? 'No'
                : ''}
            </Row>
          </>
        )}
      </Section>

      <Section title="Informed Consent">
        <div className="text-sm text-gray-600 mb-2">
          Patient agreed to all {CONSENT_CLAUSES.length} consent clauses with
          initials &ldquo;
          {(() => {
            // Try the structured consent_initials map first.
            let ci = intake.consent_initials;
            if (typeof ci === 'string') {
              try {
                const parsed = JSON.parse(ci);
                ci = parsed && typeof parsed === 'object' ? parsed : null;
              } catch (_) {
                ci = null;
              }
            }
            if (ci && typeof ci === 'object' && !Array.isArray(ci)) {
              const byKey = ci[CONSENT_CLAUSES[0].key];
              if (typeof byKey === 'string' && byKey.trim()) return byKey;
              // Any value in the map (all keys hold the same initials in the
              // current flow).
              const firstVal = Object.values(ci).find(
                (v) => typeof v === 'string' && v.trim(),
              );
              if (firstVal) return firstVal;
            }
            // Fallback: derive from the typed signature on the row so the
            // doctor always sees something meaningful even if the backend
            // didn't return the consent_initials column.
            const name = (intake.signed_name || '').trim();
            if (!name) return '';
            return name
              .split(/\s+/)
              .map((part) => part[0])
              .filter(Boolean)
              .slice(0, 3)
              .join('')
              .toUpperCase();
          })()}
          &rdquo;.
        </div>
      </Section>
    </div>
  );
};

export default PatientIntakeView;
